import {
  Component,
  OnInit,
  Input,
  Output,
  ElementRef,
  HostListener,
  ViewChild,
  forwardRef,
  EventEmitter
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { trigger, style, transition, animate } from '@angular/animations';
import { TagsFormatService } from '../tags/tags-format.service';
import { reduce, isNil } from 'ramda/es';
import { ITag } from '../../models/tag';
import * as rangy from 'rangy';
import 'rangy/lib/rangy-selectionsaverestore';

const CHIP_CLASS_NAME = 'chip';

@Component({
  selector: 'tag-editor',
  templateUrl: './tag-editor.component.html',
  styleUrls: ['./tag-editor.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition('void => *', [style({ opacity: 0 }), animate(500, style({ opacity: 1 }))]),
      transition('* => void', [animate(500, style({ opacity: 0 }))])
    ])
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditorComponent),
      multi: true
    }
  ]
})
export class EditorComponent implements OnInit, ControlValueAccessor {
  @Input() completions: any[] = [];
  @Input() role: 'text' | 'textarea' = 'text';
  @Input() tools: 'default' | 'extended' = 'default';
  @Input() readonly = false;
  @Input() disallowTags: string[] = [];
  @Input() showSaveButton: boolean = false;
  @Input() showTagSelect: boolean = true;
  @Output() valueChange$: EventEmitter<any> = new EventEmitter();
  @Input() inserts: any[] = [];
  @Input() showInserts: boolean = false;

  @ViewChild('editor') editor: ElementRef;

  @Input() _content: string = '';
  get content() {
    return this._content;
  }
  set content(val) {
    this._content = val;
    this.propagateChange(this._content);
  }

  public showToolbar: boolean;
  private lastRange: any;
  private propagateChange = (_: any) => {};
  private propagateTouch = (_: any) => {};

  public fontSizes = [
    { name: 'fontsize-1', command: 'fontSize', args: '1'},
    { name: 'fontsize-2', command: 'fontSize', args: '2'},
    { name: 'fontsize-3', command: 'fontSize', args: '3'},
    { name: 'fontsize-4', command: 'fontSize', args: '4'},
    { name: 'fontsize-5', command: 'fontSize', args: '5'},
    { name: 'fontsize-6', command: 'fontSize', args: '6'},
    { name: 'fontsize-7', command: 'fontSize', args: '7'}
  ];

  public fontNames = [
     { name: 'fontname-arial', command: 'fontName', args: 'Arial'},
     { name: 'fontname-helvetica', command: 'fontName', args: 'Helvetica'},
     { name: 'fontname-verdana', command: 'fontName', args: 'Verdana'},
     { name: 'fontname-tahoma', command: 'fontName', args: 'Tahoma'},
     { name: 'fontname-trebuchet', command: 'fontName', args: 'Trebuchet MS'},
     { name: 'fontname-times', command: 'fontName', args: 'Times New Roman'},
     { name: 'fontname-georgia', command: 'fontName', args: 'Georgia'},
     { name: 'fontname-garamond', command: 'fontName', args: 'Garamond'},
     { name: 'fontname-courier', command: 'fontName', args: 'Courier New'}
    ];

  constructor(private _elRef: ElementRef, private tagFormat: TagsFormatService) {}

  ngOnInit() {
    this.showToolbar = this.role == 'textarea';
    this.editor.nativeElement.addEventListener('paste', e => {
      e.stopPropagation();
      e.preventDefault();
      const clipboardData = e.clipboardData || (<any> window).clipboardData;
      if (clipboardData) {
        this.pasteHtmlAtCaret(this.stripHTML(clipboardData.getData('Text')), null)
      }
      return false;
    })
  }

  save() {
    this.htmlToTags();    
    this.valueChange$.emit(true);
  }

  exec(command, args = null) {
    // Temporarily make chips editable
    const chips = this.editor.nativeElement.querySelectorAll('.chip');
    chips.forEach(chip => chip.removeAttribute('contenteditable'));
    
    // Apply command
    document.execCommand(command, false, args);
    
    // Restore non-editable state
    chips.forEach(chip => chip.setAttribute('contenteditable', 'false'));
  }

  writeValue(value: any) {
    if (!isNil(value)) {
      this.content = value;
    }
  }

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any) {
    this.propagateTouch = fn;
  }

  addText(tag: ITag) {
    this.editor.nativeElement.focus();
    const html = this.tagFormat.getTagHtml(tag.tag, tag.value || tag.label);
    this.pasteHtmlAtCaret(html, false);
  }

  onKey(event: KeyboardEvent) {
    this.saveCaretPosition();
    const selection = window.getSelection();
    const element = selection.getRangeAt(0).startContainer.parentElement;
    // If the action is delete on a chip, we remove the whole chip:
    if (event.keyCode === 8 && element.className === CHIP_CLASS_NAME) {
      event.srcElement.removeChild(element);
      return;
    }
    // If the action is enter and the role is texte, disallow this action
    if (event.keyCode === 13 && this.role === 'text') {
      event.preventDefault();
      return;
    }
  }

  onClick(event: any) {
    const selection = window.getSelection();
    const element = selection.getRangeAt(0).startContainer.parentElement;
    // If the click is on a chip, we go to its end
    if (element.className == CHIP_CLASS_NAME) {
      const parent = this.getLastParent(element, CHIP_CLASS_NAME);
      this.setCaretPosition(parent, -1); // at the end of the element
    }
    this.saveCaretPosition();
  }

  onFocusOut($event: FocusEvent) {
    // Check if focus moved to another element within the same component
    const relatedTarget = $event.relatedTarget as HTMLElement;
    if (relatedTarget && this._elRef.nativeElement.contains(relatedTarget)) {
      return; // Don't process - focus is still within component
    }
    this.htmlToTags();    
  }

  @HostListener('document:click', ['$event'])
  clickout(event) {
    // When the editor's role is 'text', we hide the toolbar on focus end
    const inElement = this._elRef.nativeElement.contains(event.target);
    this.showToolbar = inElement ? true : this.role === 'text' ? false : true;
  }

  /**
   * This method tranforms the html to the inital format with tags
   */
  private htmlToTags() {
    const editorContent = this.editor.nativeElement;
    const spans = [].slice.call(editorContent.querySelectorAll(`.${CHIP_CLASS_NAME}`));
    const markers = [].slice
      .call(editorContent.querySelectorAll('.rangySelectionBoundary'))
      .filter(el => el.id != this.lastRange.markerId);
    const elements = spans.concat(markers);

    this.content = reduce((result: string, span: any) => {
      return result.replace(span.outerHTML, span.dataset.tag || '');
    }, editorContent.innerHTML)(elements)
      .replace(/&nbsp;/g, ' ')
      .replace(/<div>/gi, '<br>')
      .replace(/<\/div>/gi, '');
  }

  /**
   * This method walk the dom to find the last parent with the specified class name
   * For instance
   * DOM: .chip.first > .chip.second > .chip.thrid
   * JS: this.getLastParent($(.chip.thrid), 'chip') => .chip.first
   */
  private getLastParent(el, name) {
    return el.parentElement.className === name ? this.getLastParent(el.parentElement, name) : el;
  }

  /**
   * This method set the caret position within element
   * @param element The DOM element where the caret should be placed
   * @param position The position where we wan't to set the caret (-1 == end of the element)
   */
  private setCaretPosition(element, position: number) {
    const range = document.createRange();
    if (position === -1) {
      range.setStartAfter(element);
    } else {
      range.setStart(element, position);
    }
    range.collapse(true);
    const selection = window.getSelection();
    selection.getRangeAt(0);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  /**
   * The method saves the caret position
   */
  private saveCaretPosition() {
    const selection = rangy.getSelection();
    const range = selection.getRangeAt(0);
    this.lastRange = rangy.saveRange(range);
  }

  /**
   * This method restore the Caret position by returning a range with
   * last caret position
   */
  private restoreCaretPosition() {
    return rangy.restoreRange(this.lastRange, false);
  }

  /**
   * This method paste html to the last position of the cursor
   * in the editor
   * @param html
   * @param selectPastedContent
   */
  private pasteHtmlAtCaret(html, selectPastedContent) {
    const range = this.restoreCaretPosition();
    const element = document.createElement('div');
    element.innerHTML = html;
    const frag = document.createDocumentFragment();
    let node, lastNode;
    while ((node = element.firstChild)) {
      lastNode = frag.appendChild(node);
    }

    const firstNode = frag.firstChild;
    range.insertNode(frag);

    // Preserve the selection
    if (lastNode) {
      const newRange = range.cloneRange();
      newRange.setStartAfter(lastNode);
      if (selectPastedContent) {
        newRange.setStartBefore(firstNode);
      } else {
        newRange.collapse(true);
      }
      const selection = rangy.getSelection();
      selection.removeAllRanges();
      selection.addRange(newRange);
    }
  }

  stripHTML = value => (value.replace(/<(?:.|\n)*?>/gm, ''));
  
}



// WEBPACK FOOTER //
// ./src/app/modules/tag-editor/tag-editor.component.ts